import type { GrpcClient } from '@pushwoosh/http-client';
import { TagsServiceClient } from '@pushwoosh/rpc-service-client/pushwoosh/tags/TagsServiceClientPb';
import {
  GetTagValuesRequest,
  ListTagsRequest,
  Tag as TagProto,
} from '@pushwoosh/rpc-service-client/pushwoosh/tags/tags_pb';
import { type TagData, type ValueType } from '../types';
import TagProtoType = TagProto.Type;
import LikeOperator = GetTagValuesRequest.LikeOperator;

type TagsListParams = {
  orderBy?: 'name' | 'created';
  orderDirection?: 'asc' | 'desc';
  perPage?: number;
  page?: number;
};

type TagValuesListParams = {
  applicationCode: string;
  tagName: string;
  like: string;
  likeMatch: 'start' | 'end' | 'any';
};

export type TagsGrpcBridge = {
  list: (params: TagsListParams) => Promise<TagData[]>;
  values: (params: TagValuesListParams) => Promise<string[]>;
};

function stringifyTagType(num: number): ValueType {
  const types: Record<number, ValueType> = {
    [TagProtoType.INTEGER]: 'integer',
    [TagProtoType.STRING]: 'string',
    [TagProtoType.LIST]: 'list',
    [TagProtoType.DATE]: 'date',
    [TagProtoType.BOOLEAN]: 'boolean',
    [TagProtoType.PRICE]: 'decimal',
    [TagProtoType.VERSION]: 'version',
    [TagProtoType.TIMEZONE]: 'timezone',
  };
  return types[num];
}

export function createTagsGrpcBridge(grpcClient: GrpcClient): TagsGrpcBridge {
  const client = grpcClient.createServiceClient(TagsServiceClient);
  return {
    list: async (params) => {
      const req = new ListTagsRequest();
      if (params.orderBy) {
        req.setOrderBy(
          params.orderBy === 'name'
            ? ListTagsRequest.OrderBy.NAME
            : ListTagsRequest.OrderBy.CREATED,
        );
      }
      if (params.orderDirection) {
        req.setOrderDirection(
          params.orderDirection === 'asc'
            ? ListTagsRequest.OrderDirection.ASC
            : ListTagsRequest.OrderDirection.DESC,
        );
      }
      if (params.perPage) {
        req.setPerPage(params.perPage);
      }
      if (params.page) {
        req.setPage(params.page);
      }

      const tagsList = await client.list(req, null);
      return tagsList.getTagsList().map((tag) => ({
        name: tag.getName(),
        type: stringifyTagType(tag.getType()),
      }));
    },
    values: async (params) => {
      const req = new GetTagValuesRequest()
        .setApplication(params.applicationCode)
        .setName(params.tagName)
        .setLike(params.like)
        .setLikeOp(
          // eslint-disable-next-line no-nested-ternary
          params.likeMatch === 'start'
            ? LikeOperator.STARTS_WITH
            : params.likeMatch === 'end'
              ? LikeOperator.ENDS_WITH
              : LikeOperator.CONTAINS,
        );
      const resp = await client.getTagValues(req, null);
      return resp.getValuesList().map((value) => value.getValue().trim());
    },
  };
}
